/** Группировка ленты по дням — то, как её читают: «29 августа, −54 $».
 *
 * Итог дня считаем в базовой валюте: складывать донги с долларами нельзя, а
 * base для того и существует. Доход прибавляем, расход вычитаем — знак «+/−»
 * в заголовке дня и есть ответ на «этот день меня обрадовал или нет».
 */
import type { Operation } from "./api.ts";

export type DayGroup = { day: string; label: string; total: string; rows: Operation[] };

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня",
                "июля", "августа", "сентября", "октября", "ноября", "декабря"];

/** «в августе» — предложный падеж, для заголовка «Траты в августе».
 *  Отдельный список: родительный из MONTHS сюда не годится. */
const MONTHS_IN = ["январе", "феврале", "марте", "апреле", "мае", "июне",
                   "июле", "августе", "сентябре", "октябре", "ноябре", "декабре"];

const MONTHS_NOM = ["январь", "февраль", "март", "апрель", "май", "июнь",
                    "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"];

/** «август» — именительный, для заголовка «Итог: август». */
export function monthName(day: string): string {
  return MONTHS_NOM[Number(day.slice(5, 7)) - 1] ?? "месяц";
}

export function monthIn(day: string): string {
  return MONTHS_IN[Number(day.slice(5, 7)) - 1] ?? "этом месяце";
}

export function groupByDay(rows: Operation[]): DayGroup[] {
  const buckets = new Map<string, Operation[]>();
  for (const row of rows) {
    const day = row.occurred_at.slice(0, 10);
    const bucket = buckets.get(day);
    if (bucket) bucket.push(row);
    else buckets.set(day, [row]);
  }
  return [...buckets.entries()].map(([day, group]) => ({
    day,
    label: dayLabel(day),
    total: dayTotal(group),
    rows: group,
  }));
}

/** «29 августа» — день словами. Общий с заголовками ленты, чтобы дата
 *  в карточке и в ленте выглядела одинаково. */
export function dayLabel(day: string): string {
  const [, month, date] = day.split("-");
  return `${Number(date)} ${MONTHS[Number(month) - 1]}`;
}

/** Сумма дня строкой. Считаем в копейках целыми — чтобы float не съел копейку. */
function dayTotal(rows: Operation[]): string {
  let cents = 0n;
  for (const row of rows) {
    const sign = row.op_type === "income" ? 1n : -1n;
    cents += sign * toCents(row.amount_base);
  }
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const whole = abs / 100n;
  const rest = abs % 100n;
  const frac = rest === 0n ? "" : `.${String(rest).padStart(2, "0")}`;
  return `${negative ? "-" : ""}${whole}${frac}`;
}

function toCents(amount: string): bigint {
  const [whole = "0", frac = ""] = amount.replace("-", "").split(".");
  const cents = BigInt(whole) * 100n + BigInt((frac + "00").slice(0, 2));
  return amount.startsWith("-") ? -cents : cents;
}
